import { closestCenter, DndContext, MouseSensor, useSensor } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Alert, Box, Button, Group, Loader, Paper, Stack, Text, Title, Tooltip } from '@mantine/core'
import { useListState } from '@mantine/hooks'
import { IconAlertCircle, IconPlus } from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useState } from 'react'

import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'
import { useSetupProgressTracker } from '@/hooks/useSetupProgressTracker'
import type { ESKResponse, GetSponsorTopicsResponse, TopicResponse } from '@/services/api/api.types'

import { ConfirmChangesModal } from './ConfirmChangesModal'
import { TopicEntry } from './TopicEntry'
import { useSidekicks } from './useSidekicks'
import {
  calculatePriorities,
  calculateTopicModuleCounts,
  calculateTopicWarnings,
  compareTopicArrays,
  findDuplicateNumber,
  getSidekickId,
  pollUntilDataMatches,
  sortTopicsByPriority,
  validateTopics,
} from './utils'

interface TopicsPanelProps {
  showTutorialText: boolean
  showModuleCounts: boolean
  registerUnsavedChangesChecker: (tabKey: 'topics', checker: () => boolean, onDiscard: () => void) => void
}

// Toggle this flag to bypass the confirmation dialog
const BYPASS_CONFIRMATION_DIALOG = true

export function TopicsPanel({
  showTutorialText = false,
  showModuleCounts = false,
  registerUnsavedChangesChecker,
}: TopicsPanelProps) {
  const { api } = useGlobalState()
  const [isPending, setIsPending] = useState(false)
  const isMobile = useCheckMobileScreen()

  // Get setup progress tracker for marking setup steps complete
  const { markStepComplete } = useSetupProgressTracker()

  // Allows us to use the drag handle as a menu button as well
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 1,
    },
  })

  const topicsQuery = useQuery({
    queryKey: ['sponsor', 'topics'],
    queryFn: () => api.getSponsorTopics(),
    refetchOnMount: 'always',
    staleTime: 0,
  })

  const contentModulesQuery = useQuery({
    queryKey: ['cm', 'list'],
    queryFn: () => api.getContentModules(),
    initialData: [],
  })

  const [originalTopics, setOriginalTopics] = useState<GetSponsorTopicsResponse>([])
  const [topics, topicHandlers] = useListState<TopicResponse>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [topicsWithWarnings, setTopicsWithWarnings] = useState<Set<string>>(new Set())
  const [newTopicId, setNewTopicId] = useState<string | null>(null)
  const [newSubtopicId, setNewSubtopicId] = useState<string | null>(null)

  const {
    allSidekicks: baseSidekicks,
    selectedMsks,
    defaultMsk,
    isLoading: sidekicksLoading,
    error: sidekicksError,
    sidekickOptionToEsk,
  } = useSidekicks()

  // Calculate sidekick availability based on local topics state (not server data)
  const allSidekicks = useMemo(() => {
    const assignedEskIds = new Set(topics.map(topic => topic.skId).filter(Boolean))

    return baseSidekicks.map(sidekick => ({
      ...sidekick,
      isAvailable: sidekick.isSelected ? false : !assignedEskIds.has(getSidekickId(sidekick)),
    }))
  }, [baseSidekicks, topics])

  // Computed Properties
  // ------------------------------------------------------------
  const unassignedTopics = useMemo(() => {
    return topics.filter(topic => !topic.skId)
  }, [topics])

  // Get the default MSK for PSK avatar display
  const defaultMskSidekick = useMemo(() => {
    if (!defaultMsk || !selectedMsks.length) return null
    return selectedMsks.find(msk => getSidekickId(msk) === defaultMsk) || null
  }, [selectedMsks, defaultMsk])

  // Validation Logic
  // ------------------------------------------------------------

  const validationErrors = useMemo(() => {
    return validateTopics(topics)
  }, [topics])

  const hasValidationErrors = useMemo(() => {
    return validationErrors.length > 0
  }, [validationErrors])

  // Update State from API
  // ------------------------------------------------------------
  useEffect(() => {
    if (topicsQuery.data) {
      const sortedTopics = sortTopicsByPriority(topicsQuery.data)
      topicHandlers.setState(sortedTopics)
      setOriginalTopics(sortedTopics)
    }
  }, [topicsQuery.data])
  // Count modules per topic using direct topic ID mapping
  const topicModuleCounts = useMemo(() => {
    return calculateTopicModuleCounts(topics, contentModulesQuery.data || [])
  }, [topics, contentModulesQuery.data])

  // Update the warning effect to use ID-based module counts
  useEffect(() => {
    const warnings = calculateTopicWarnings(topics, originalTopics, topicModuleCounts)
    setTopicsWithWarnings(warnings)
  }, [topics, topicModuleCounts, originalTopics])

  // Topic Functionality
  // ------------------------------------------------------------
  const handleAddTopic = () => {
    const newTopicId = nanoid()

    const newTopic: TopicResponse = {
      id: newTopicId,
      key: newTopicId,
      displayName: `Topic ${topics.length + 1}`,
      subtopics: [],
      priority: 0, // Add at the end of the list
      skId: 'PSK',
      notRemovable: false, // New topics are removable by default
    }
    topicHandlers.append(newTopic)

    // Set the new topic ID to trigger auto-focus
    setNewTopicId(newTopicId)
  }

  const handleDuplicateTopic = (topicId: string) => {
    const topicToDuplicate = topics.find(t => t.id === topicId)

    if (!topicToDuplicate) return

    const newTopicId = nanoid()
    const nextNumber = findDuplicateNumber(topics, topicToDuplicate.displayName)

    const newTopic: TopicResponse = {
      ...topicToDuplicate,
      id: newTopicId,
      key: newTopicId,
      displayName: `${topicToDuplicate.displayName} (${nextNumber})`,
      subtopics: topicToDuplicate.subtopics?.map(subtopic => ({
        ...subtopic,
        id: nanoid(),
        key: nanoid(),
      })),
      priority: 0, // Add at the end of the list
      skId: null, // Leave empty for user to select
      notRemovable: false, // Duplicated topics are removable by default
    }

    topicHandlers.append(newTopic)
    // Set the new topic ID to trigger auto-focus
    setNewTopicId(newTopicId)
  }

  const handleDeleteTopic = (topicId: string) => {
    if (topicModuleCounts[topicId] > 0) {
      return
    }

    // Remove the topic from the local state
    topicHandlers.setState(prevTopics => prevTopics.filter(topic => topic.id !== topicId))
  }

  const handleTopicNameChange = (topicId: string, newName: string) => {
    topicHandlers.setState(prevTopics =>
      prevTopics.map(topic =>
        topic.id === topicId
          ? {
              ...topic,
              displayName: newName,
            }
          : topic,
      ),
    )
  }

  const handleTopicExpertChange = (topicId: string, _currentEskId: string | null, newEskId: string) => {
    // Validate that the new sidekick is available before assigning
    if (newEskId && newEskId !== 'PSK') {
      const sidekick = allSidekicks.find(s => getSidekickId(s) === newEskId)
      if (!sidekick?.isAvailable) {
        console.warn(`Cannot assign unavailable sidekick ${newEskId} to topic ${topicId}`)
        return
      }
    }

    // Update the topic's skId field directly
    topicHandlers.setState(prev =>
      prev.map(topic =>
        topic.id === topicId
          ? {
              ...topic,
              skId: newEskId,
            }
          : topic,
      ),
    )
  }

  // Subtopic Functionality
  // ------------------------------------------------------------
  const handleAddSubtopic = (topicId?: string) => {
    if (!topicId) {
      return
    }

    const newSubtopicId = nanoid()

    topicHandlers.setState(prevTopics =>
      prevTopics.map(topic =>
        topic.id === topicId
          ? {
              ...topic,
              subtopics: [
                ...(topic.subtopics ?? []),
                {
                  id: newSubtopicId,
                  key: newSubtopicId,
                  displayName: `Subtopic ${(topic.subtopics ?? []).length + 1}`,
                },
              ],
            }
          : topic,
      ),
    )

    // Set the new subtopic ID to trigger auto-focus
    setNewSubtopicId(newSubtopicId)
  }

  const handleRemoveSubtopic = (topicId?: string, subtopicId?: string) => {
    if (!topicId || !subtopicId) {
      return
    }

    topicHandlers.setState(prevTopics =>
      prevTopics.map(topic =>
        topic.id === topicId
          ? {
              ...topic,
              subtopics: topic.subtopics?.filter(subtopic => subtopic.id !== subtopicId),
            }
          : topic,
      ),
    )
  }

  const handleSubtopicNameChange = (topicId: string, subtopicId: string, newName: string) => {
    topicHandlers.setState(prevTopics =>
      prevTopics.map(topic =>
        topic.id === topicId
          ? {
              ...topic,
              subtopics: topic.subtopics?.map(subtopic =>
                subtopic.id === subtopicId
                  ? {
                      ...subtopic,
                      displayName: newName,
                    }
                  : subtopic,
              ),
            }
          : topic,
      ),
    )
  }

  // Change Detection
  // ------------------------------------------------------------
  const hasTopicChanges = useMemo(() => {
    return !compareTopicArrays(originalTopics, topics)
  }, [originalTopics, topics])

  // Helper function to get ESKs that need to be updated based on topic skId assignments
  const getEsksFromTopics = useCallback(
    (topicsToProcess: TopicResponse[]) => {
      const esksToUpdate: typeof allSidekicks = []

      topicsToProcess.forEach(topic => {
        if (topic.skId) {
          // Find the sidekick option that matches this skId
          const sidekickOption = allSidekicks.find(sk => getSidekickId(sk) === topic.skId)
          if (sidekickOption) {
            // Convert to ESK format and set the topicId
            const eskWithTopicId = {
              ...sidekickOption,
              topicId: topic.id || null,
            }
            esksToUpdate.push(eskWithTopicId)
          }
        }
      })

      return esksToUpdate
    },
    [allSidekicks],
  )

  // Check if ESK assignments have changed
  const hasEskChanges = useMemo(() => {
    const originalEskAssignments = new Map(originalTopics.map(topic => [topic.id, topic.skId]))
    const currentEskAssignments = new Map(topics.map(topic => [topic.id, topic.skId]))

    // Check if any assignments have changed
    for (const [topicId, skId] of currentEskAssignments) {
      if (originalEskAssignments.get(topicId) !== skId) {
        return true
      }
    }

    // Check if any original assignments were removed
    for (const [topicId, skId] of originalEskAssignments) {
      if (currentEskAssignments.get(topicId) !== skId) {
        return true
      }
    }

    return false
  }, [originalTopics, topics])

  const hasChanges = useMemo(() => {
    return hasTopicChanges || hasEskChanges
  }, [hasTopicChanges, hasEskChanges])

  // ------------------------------------------------------------
  const handleCancel = useCallback(() => {
    topicHandlers.setState(originalTopics)
  }, [originalTopics, topicHandlers])

  // Register unsaved changes checker with parent
  useEffect(() => {
    registerUnsavedChangesChecker('topics', () => hasChanges, handleCancel)
  }, [registerUnsavedChangesChecker, hasChanges, handleCancel])

  // Helper Functions
  // ------------------------------------------------------------
  const pollAndMarkTopicsComplete = async (expectedTopics: TopicResponse[]) => {
    // Poll until server data matches our expected state
    await pollUntilDataMatches(
      async () => {
        await api.queryClient.invalidateQueries({ queryKey: ['sponsor', 'topics'] })
        const refetchResult = await topicsQuery.refetch()
        return refetchResult.data || []
      },
      serverData => compareTopicArrays(expectedTopics, serverData),
    )

    // Mark topics step as complete after successful update
    markStepComplete('defineTopics')
  }

  const updateTopicsMutation = useMutation({
    mutationFn: async (params: { topics: TopicResponse[]; esks?: ESKResponse[] }): Promise<void> => {
      try {
        await api.updateTopics(params)
      } catch (error) {
        console.error('Error updating topics:', error)
        if (topicsQuery.data) {
          topicHandlers.setState(topicsQuery.data)
        }
        throw error
      }
    },
    onSuccess: async (_, params) => {
      await pollAndMarkTopicsComplete(params.topics)
    },
    onError: error => {
      console.error('Mutation error:', error)
    },
  })

  const handleConfirmApply = async () => {
    setIsPending(true)
    try {
      const updatedTopics = calculatePriorities(topics)

      let esksToUpdate = undefined
      if (hasEskChanges) {
        const eskSidekicks = getEsksFromTopics(updatedTopics)
        esksToUpdate = eskSidekicks.map(sidekick => sidekickOptionToEsk(sidekick))
      }

      await updateTopicsMutation.mutateAsync({
        topics: updatedTopics,
        ...(esksToUpdate && { esks: esksToUpdate }),
      })

      setOriginalTopics([...topics])
    } catch (error) {
      console.error('Error applying changes:', error)
      // Revert to original state on error
      topicHandlers.setState(originalTopics)
    } finally {
      setIsPending(false)
      setShowConfirmModal(false)
      setNewTopicId(null)
      setNewSubtopicId(null)
    }
  }

  // Reorder Topics
  // ------------------------------------------------------------
  const handleDragEnd = (event: any) => {
    const { active, over } = event

    if (active.id !== over.id) {
      const oldIndex = topics.findIndex(topic => topic.id === active.id)
      const newIndex = topics.findIndex(topic => topic.id === over.id)

      topicHandlers.setState(arrayMove(topics, oldIndex, newIndex))
    }
  }

  const handleMoveUp = (index: number) => {
    if (index > 0) {
      topicHandlers.setState(arrayMove(topics, index, index - 1))
    }
  }

  const handleMoveDown = (index: number) => {
    if (index < topics.length - 1) {
      topicHandlers.setState(arrayMove(topics, index, index + 1))
    }
  }

  return (
    <Stack>
      <Paper radius="md" p={isMobile ? 'md' : 'lg'} shadow="sm" px={isMobile ? 'md' : 'xl'}>
        <Group justify="space-between" align="center" mb="md">
          {isMobile ? (
            <Text fw={600} size="xl" mb="md">
              Define Topics for Sidekicks
            </Text>
          ) : (
            <Title order={4}>Define Topics for Sidekicks</Title>
          )}
          <Group>
            <Button variant="outline" onClick={handleCancel} disabled={!hasChanges || isPending}>
              Cancel
            </Button>
            <Tooltip label="Save becomes available when you make changes. Save or Cancel any changes.">
              <Button
                onClick={() => (BYPASS_CONFIRMATION_DIALOG ? handleConfirmApply() : setShowConfirmModal(true))}
                disabled={unassignedTopics.length > 0 || !hasChanges || hasValidationErrors}
                loading={BYPASS_CONFIRMATION_DIALOG ? isPending : false}>
                Save
              </Button>
            </Tooltip>
          </Group>
        </Group>

        {showTutorialText && (
          <>
            <Text size="md" c="dimmed" mb="xs">
              Create a roster of Sidekicks by editing the Topics and Sub-Topics or adding more with the &quot;Add
              Topic&quot; button. Then drag and drop your Topics (and their Sidekicks) into order of importance.
            </Text>
          </>
        )}

        {validationErrors.length > 0 && (
          <Alert
            variant="light"
            color="red"
            radius="md"
            title="Validation Errors"
            icon={<IconAlertCircle size={16} />}
            py="xs"
            mb="md">
            <Stack gap="xs">
              {validationErrors.map((error, index) => (
                <Text key={index} size="sm">
                  {error}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        <Group justify="space-between" align="center" mb="md">
          <Group>
            <Button leftSection={<IconPlus size={14} />} onClick={handleAddTopic}>
              Add Topic
            </Button>
          </Group>
        </Group>

        <Group justify="space-between" align="top">
          {/* Only show loader during initial loading, not when refetching with no topics */}
          {(topicsQuery.isLoading && !topicsQuery.data) || sidekicksLoading ? (
            <Group justify="center" p="xl">
              <Loader />
            </Group>
          ) : topicsQuery.isError || sidekicksError ? (
            <Text c="red">Error loading topics or sidekicks</Text>
          ) : !topics.length ? (
            <Box
              py="xl"
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Text c="dimmed" ta="center" size="xl">
                No topics found
              </Text>
            </Box>
          ) : (
            <DndContext onDragEnd={handleDragEnd} collisionDetection={closestCenter} sensors={[mouseSensor]}>
              <SortableContext items={topics.map(topic => topic.id ?? '')} strategy={verticalListSortingStrategy}>
                <Stack gap="md" style={{ width: '100%' }}>
                  {topics.map((item, index) => (
                    <TopicEntry
                      id={item.id ?? ''}
                      key={item.id ?? index}
                      item={item}
                      index={index}
                      menuProps={{
                        onMoveUp: () => handleMoveUp(index),
                        onMoveDown: () => handleMoveDown(index),
                        onDelete: () => handleDeleteTopic(item.id ?? ''),
                        onDuplicate: () => handleDuplicateTopic(item.id ?? ''),
                        canMoveUp: index > 0,
                        canMoveDown: index < topics.length - 1,
                        canDelete: topicModuleCounts[item.id ?? ''] === 0 && !item.notRemovable,
                        canDuplicate: topics.length < allSidekicks.length,
                      }}
                      subtopics={item.subtopics}
                      eskOptions={allSidekicks}
                      selectedExpertSidekick={item.skId ?? undefined}
                      onExpertSidekickChange={handleTopicExpertChange}
                      moduleCount={topicModuleCounts[item.id ?? ''] ?? 0}
                      onDisplayNameChange={handleTopicNameChange}
                      onUpdateSubtopicName={(subtopicId, newName) =>
                        handleSubtopicNameChange(item.id ?? '', subtopicId, newName)
                      }
                      onRemoveSubtopic={subtopicId => handleRemoveSubtopic(item.id, subtopicId)}
                      onAddSubtopic={handleAddSubtopic}
                      showWarning={topicsWithWarnings.has(item.id ?? '')}
                      shouldFocus={item.id === newTopicId}
                      newSubtopicId={newSubtopicId}
                      showModuleCounts={showModuleCounts}
                      defaultMskSidekick={defaultMskSidekick}
                    />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>
          )}
        </Group>
      </Paper>

      <ConfirmChangesModal
        title="Are you sure?"
        description="Making changes to the Topics can impact the experience of Members who have already set up their topics."
        opened={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmApply}
        isLoading={isPending}
      />
    </Stack>
  )
}
